import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft, TrendingUp, TrendingDown, Clock, Users,
  CheckCircle2, XCircle, Star, AlertCircle
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { safeUUID } from "@/lib/utils";

export default function WorkflowAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [workflow, setWorkflow] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    if (!id) return;
    
    // Validate UUID
    if (!safeUUID(id)) {
      console.error(`Invalid workflow ID: "${id}"`);
      toast({
        title: "Invalid Workflow ID",
        description: "The workflow ID is invalid.",
        variant: "destructive",
      });
      navigate('/admin/templates/workflows');
      return;
    }

    setLoading(true);
    try {
      // Load workflow
      const { data: workflowData, error: workflowError } = await supabase
        .from('workflows')
        .select('*')
        .eq('id', id)
        .single();

      if (workflowError) throw workflowError;
      setWorkflow(workflowData);

      // Load analytics data
      const { data: completions, error: completionsError } = await supabase
        .from('workflow_completion_tracking')
        .select('*')
        .eq('workflow_id', id);

      if (completionsError) throw completionsError;

      // Calculate analytics
      const total = completions?.length || 0;
      const completed = completions?.filter(c => c.completed_at).length || 0;
      const abandoned = completions?.filter(c => c.abandoned).length || 0;
      const inProgress = total - completed - abandoned;

      const completedWithTime = completions?.filter(c => c.completed_at && c.time_to_complete_minutes) || [];
      const avgTime = completedWithTime.length > 0
        ? completedWithTime.reduce((acc, c) => acc + (c.time_to_complete_minutes || 0), 0) / completedWithTime.length
        : 0;

      const ratings = completions?.filter(c => c.user_rating).map(c => c.user_rating) || [];
      const avgRating = ratings.length > 0
        ? ratings.reduce((acc, r) => acc + r, 0) / ratings.length
        : 0;

      // Phase completion funnel
      const phaseCounts = new Map<number, { started: number; completed: number }>();
      const phasesArray = Array.isArray(workflowData.phases) ? workflowData.phases : [];
      for (const completion of completions || []) {
        const phases = completion.phases_completed || 0;
        for (let i = 1; i <= phasesArray.length; i++) {
          if (!phaseCounts.has(i)) {
            phaseCounts.set(i, { started: 0, completed: 0 });
          }
          const counts = phaseCounts.get(i)!;
          counts.started++;
          if (i <= phases) {
            counts.completed++;
          }
        }
      }

      const phaseData = Array.from(phaseCounts.entries()).map(([phase, counts]) => ({
        phase: `Phase ${phase}`,
        started: counts.started,
        completed: counts.completed,
        dropoff: counts.started - counts.completed,
      }));

      // Time series data (launches over time)
      const launchesByMonth = new Map<string, number>();
      for (const completion of completions || []) {
        const month = new Date(completion.started_at).toISOString().substring(0, 7);
        launchesByMonth.set(month, (launchesByMonth.get(month) || 0) + 1);
      }

      const timeSeriesData = Array.from(launchesByMonth.entries())
        .sort()
        .map(([month, count]) => ({
          month,
          launches: count,
        }));

      setAnalytics({
        total,
        completed,
        abandoned,
        inProgress,
        completionRate: total > 0 ? (completed / total) * 100 : 0,
        abandonmentRate: total > 0 ? (abandoned / total) * 100 : 0,
        avgTimeMinutes: avgTime,
        avgRating,
        ratingCount: ratings.length,
        phaseData,
        timeSeriesData,
        statusData: [
          { name: 'Completed', value: completed, color: '#10b981' },
          { name: 'In Progress', value: inProgress, color: '#3b82f6' },
          { name: 'Abandoned', value: abandoned, color: '#ef4444' },
        ],
      });
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (!workflow || !analytics) {
    return (
      <div className="container mx-auto p-6">
        <p>Workflow not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/templates/workflows')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">{workflow.title}</h1>
            <p className="text-muted-foreground">Workflow Analytics</p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Launches</p>
              <p className="text-2xl font-bold">{analytics.total}</p>
            </div>
            <Users className="h-8 w-8 text-muted-foreground" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {analytics.completionRate.toFixed(0)}%
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Time</p>
              <p className="text-2xl font-bold">
                {Math.round(analytics.avgTimeMinutes)} min
              </p>
            </div>
            <Clock className="h-8 w-8 text-muted-foreground" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Rating</p>
              <p className="text-2xl font-bold flex items-center gap-1">
                {analytics.avgRating.toFixed(1)}
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              </p>
            </div>
            <span className="text-xs text-muted-foreground">
              ({analytics.ratingCount} ratings)
            </span>
          </div>
        </Card>
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Status Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {analytics.statusData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>

          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <Badge variant="outline">{analytics.completed}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Completed</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" />
                <Badge variant="outline">{analytics.inProgress}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">In Progress</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                <Badge variant="outline">{analytics.abandoned}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Abandoned</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Launches Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="launches" stroke="#3b82f6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Phase Completion Funnel */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Phase Completion Funnel</h2>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={analytics.phaseData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="phase" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="started" fill="#3b82f6" name="Started" />
            <Bar dataKey="completed" fill="#10b981" name="Completed" />
            <Bar dataKey="dropoff" fill="#ef4444" name="Drop-off" />
          </BarChart>
        </ResponsiveContainer>

        {analytics.phaseData.length > 0 && (
          <div className="mt-4 p-4 bg-muted rounded">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Drop-off Analysis</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Identify phases where users struggle or abandon the workflow.
                  Consider adding more guidance or simplifying these phases.
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
